import useSWR from 'swr';

//hooks
import useUser from '@/hooks/swr/useUser';

//types
import type { SWRConfiguration } from 'swr';
import type { PlanFrequencyType } from '@/types/pricingTypes/planType';
import type {
    RawPricesType,
    FormattedPricesType,
    PlanStructureType,
    PlanVariants,
    PlanDataType,
    FormattedPlan
} from '@/utils/hooks/swr/pricing/usePrices/types';

// Utils
import { get } from '@/utils/client';

// Routes
import { BILLING_PRICES } from '@/routes/apiEndpoints';

export function formatPricesData(data: RawPricesType) {
    return Object.keys(data).reduce((formattedData, frequency: PlanFrequencyType) => {
        formattedData[frequency] = Object.keys(data[frequency]).reduce(
            (innerFormattedData, variant: PlanVariants) => {
                innerFormattedData[variant] = data[frequency][variant].reduce(
                    (productData, plan: PlanDataType) => {
                        productData[plan.features.profiles] = { ...plan };
                        return productData;
                    },
                    {} as FormattedPlan
                );

                return innerFormattedData;
            },
            {} as PlanStructureType
        );

        return formattedData;
    }, {} as FormattedPricesType);
}

const usePrices = (options?: SWRConfiguration<FormattedPricesType, Error>) => {
    const { user } = useUser();

    const {
        data,
        mutate: mutatePrices,
        error: errorPrices,
        isLoading
    } = useSWR<FormattedPricesType, Error>(
        [BILLING_PRICES, user?.id],
        ([url]) =>
            get(url).then((res: { data: RawPricesType }) => {
                const data = res.data;
                if ('_debugbar' in data) {
                    delete data._debugbar; //needed for local development only since debugbar isn't returned in production
                }
                return formatPricesData(data);
            }),
        {
            shouldRetryOnError: false,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    return {
        prices: data,
        mutatePrices,
        errorPrices,
        isLoading
    };
};
export default usePrices;
